import { useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { client, useI18n } from 'iomart-hooks';
import { useSnackbar } from 'notistack';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useProduct } from 'iomart-hooks';
import {
  getProductDefaultValues,
  productFormSchema,
  buildProductSubmitPayload,
  type ProductFormData,
} from './productFormUtils';
import { ProductFormContent } from './ProductFormContent';

type AddProductDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: (item: any) => void;
};

export function AddProductDrawer({ open, onClose, onSuccess }: AddProductDrawerProps) {
  const { t } = useI18n(false);
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const { createItem, updateItem } = useProduct(false);

  const methods = useForm<ProductFormData>({
    resolver: yupResolver(productFormSchema) as any,
    defaultValues: getProductDefaultValues(null),
  });

  const handleClose = () => {
    if (!submitting) {
      methods.reset(getProductDefaultValues(null));
      onClose();
    }
  };

  const handleSubmit = methods.handleSubmit(async (data: ProductFormData) => {
    setSubmitting(true);
    try {
      const payload = await buildProductSubmitPayload(data, undefined);
      const { __afterCreate, ...formData } = payload as any;
      const result = await createItem(formData);
      if (result?.id && __afterCreate) {
        await __afterCreate({
          id: result.id,
          result,
          hook: { updateItem },
          hooks: [],
        });
      }
      if (result?.id) {
        let activated = false;
        try {
          const activateResult = await client.mutation({
            activateProduct: {
              count: true,
              succeed: true,
              failed: true,
              __args: { ids: [result.id] },
            },
          });
          activated =
            activateResult?.activateProduct?.succeed?.includes(result.id) ?? false;
        } catch (activateErr) {
          console.error('Activate product failed:', activateErr);
        }
        if (!activated) {
          enqueueSnackbar(
            t('Product was created but not activated. Please activate it manually.'),
            { variant: 'warning' }
          );
          handleClose();
          return;
        }
        onSuccess(result);
        handleClose();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 720 }, maxWidth: '100%' },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{t('Add product')}</Typography>
          <IconButton size="small" onClick={handleClose} disabled={submitting}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Box>
        <FormProvider {...methods}>
          <Scrollbar sx={{ flex: 1, minHeight: 0 }}>
            <Box sx={{ p: 2 }}>
              <ProductFormContent
                showProductsBlock
                showPrivateFilesBlock={false}
                stackLayout
              />
            </Box>
          </Scrollbar>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Button color="inherit" onClick={handleClose} disabled={submitting}>
              {t('Cancel')}
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
              {t('Save and Activate')}
            </Button>
          </Stack>
        </FormProvider>
      </Box>
    </Drawer>
  );
}
